import { Theme } from "@mui/material";

// Same vertical box as MUI `FormControl` / `Select` `size="small"` (OutlinedInput).
export const AUDIO_PLAYER_CONTROL_HEIGHT_PX = 40;

const titleTextPill = (theme: Theme) =>
  ({
    display: "inline-block",
    width: "fit-content",
    maxWidth: "100%",
    boxSizing: "border-box",
    m: 0,
    fontWeight: 700,
    fontSize: { xs: "0.9375rem", sm: "1.125rem", md: "1.1875rem" },
    color: "#FFFFFF",
    backgroundColor: theme.palette.mode === "dark" ? "#2E3192" : "#7B68EE",
    px: { xs: 2.5, sm: 2.75 },
    py: { xs: 1.25, sm: 1.125 },
    borderRadius: { xs: "14px", sm: "16px" },
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflow: "visible",
    ...(theme.palette.mode === "dark"
      ? {
          boxShadow:
            "0 2px 14px rgba(12, 16, 48, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.06)",
        }
      : {}),
    [theme.breakpoints.down("sm")]: {
      wordBreak: "normal",
      overflowWrap: "break-word",
      lineHeight: 1.4,
    },
  }) as const;

const titleTextLegacy = (theme: Theme) =>
  ({
    fontWeight: 600,
    color: "#06B6D4",
    whiteSpace: "normal",
    wordBreak: "break-word",
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      wordBreak: "normal",
      overflowWrap: "break-word",
      lineHeight: 1.35,
    },
  }) as const;

export const audioPlayerStyles = (
  theme: Theme,
  embedded = false,
  pillTitle = false,
) => {
  const containerBase = {
    display: "flex" as const,
    flexDirection: "column" as const,
    p: "24px",
    borderRadius: "18px",
    overflow: "hidden" as const,
    [theme.breakpoints.down("sm")]: {
      p: "14px",
      borderRadius: "14px",
    },
  };
  const container = embedded
    ? {
        ...containerBase,
        maxWidth: "100%",
        width: "100%" as const,
        backgroundColor: "transparent",
      }
    : {
        ...containerBase,
        maxWidth: 250,
        width: "auto" as const,
        backgroundColor: theme.palette.audioPlayer.bg,
        [theme.breakpoints.down("sm")]: {
          maxWidth: 220,
        },
      };
  return {
    container,
    header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    mb: 2,
    gap: 12,
    [theme.breakpoints.up("sm")]: {
      gap: "49px",
    },
    [theme.breakpoints.down("sm")]: {
      mb: 1.25,
      gap: 8,
    },
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
    gap: 8,
    flexWrap: "nowrap",
    position: "relative" as const,
    zIndex: 1,
    boxSizing: "border-box",
  },
  text: pillTitle ? titleTextPill(theme) : titleTextLegacy(theme),
  speedSelect: {
    width: "65px",
    fontSize: "0.75rem",
    borderRadius: "12px",
    minHeight: AUDIO_PLAYER_CONTROL_HEIGHT_PX,
    height: AUDIO_PLAYER_CONTROL_HEIGHT_PX,
    boxSizing: "border-box" as const,
    [theme.breakpoints.down("sm")]: {
      width: "64px",
      minWidth: "64px",
      fontSize: "0.75rem",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#06B6D4",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#06B6D4",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#06B6D4",
    },
    "& .MuiSvgIcon-root": {
      color: "#06B6D4",
    },
    color: "#06B6D4",
  },
  transcriptButton: {
    fontSize: { xs: "0.7rem", sm: "0.75rem" },
    textTransform: "none" as const,
    borderRadius: "12px",
    borderColor: "#06B6D4",
    color: "#06B6D4",
    px: { xs: 0.75, sm: 1.25 },
    py: 0,
    minWidth: "auto",
    minHeight: AUDIO_PLAYER_CONTROL_HEIGHT_PX,
    height: AUDIO_PLAYER_CONTROL_HEIGHT_PX,
    boxSizing: "border-box" as const,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap" as const,
    lineHeight: 1,
    flexShrink: 0,
    "&:hover": {
      borderColor: "#0891b2",
      backgroundColor: "rgba(6, 182, 212, 0.06)",
    },
  },
  audioControlsContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  playButton: {
    mr: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  waveSurferContainer: {
    flex: 1,
    overflow: "hidden",
  },
  timeDisplay: {
    display: "flex",
    justifyContent: "space-between",
    mt: "12px",
    [theme.breakpoints.down("sm")]: {
      mt: "8px",
    },
  },
  timeTextLeft: {
    color: theme.palette.audioPlayer.timeColor,
    minWidth: "40px",
    textAlign: "left",
    fontSize: "16px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "32px",
      fontSize: "13px",
    },
  },
  timeTextRight: {
    color: theme.palette.audioPlayer.timeColor,
    minWidth: "40px",
    textAlign: "right",
    fontSize: "16px",
    [theme.breakpoints.down("sm")]: {
      minWidth: "32px",
      fontSize: "13px",
    },
  },
  };
};
